import React, { useState, useMemo, useEffect } from "react";
import FilterBar from "./FilterBar";
import UserTable from "./UserTable";
import UserModal from "./UserModal";
import Swal from "sweetalert2";
import {
  getCompaniesList,
  updateCompanyStatus,
  updateCompanyPlan,
} from "../../Backend/usersFunction";
import {
  pageShellClass,
  pageContentClass,
} from "../../Container/PageShell/pageShellClasses";

const Users = () => {
  // --- 1. STATE MANAGEMENT ---
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [dateRange, setDateRange] = useState(365);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const itemsPerPage = 8;

  // --- 2. DYNAMIC DATA FETCHING ---
  const loadCompanies = async () => {
    setIsLoading(true);
    const result = await getCompaniesList();
    if (result.success) {
      setUsers(result.data);
    } else {
      console.error("Failed to load orchestrated companies:", result.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  // --- 3. ADVANCED FILTERING ENGINE ---
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const s = searchTerm.toLowerCase();

      const matchesSearch =
        (user.name && user.name.toLowerCase().includes(s)) ||
        (user.email && user.email.toLowerCase().includes(s)) ||
        (user.companyName && user.companyName.toLowerCase().includes(s)) ||
        (user.mobile && user.mobile.includes(s));

      const isActive = user.status === true || user.status === "Active";
      const matchesStatus =
        filterStatus === "All" ||
        (filterStatus === "Active" && isActive) ||
        (filterStatus === "Inactive" && !isActive);

      const now = new Date();
      const joinedDate = user.createdAt ? new Date(user.createdAt) : new Date();
      const diffTime = Math.abs(now - joinedDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const matchesDate = diffDays <= dateRange;

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [users, searchTerm, filterStatus, dateRange]);

  // --- 4. PAGINATION ---
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const currentData = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, dateRange]);

  // --- 5. ACTION HANDLERS ---

  const handleStatusToggle = async (userId, newStatus) => {
    // 1. Local state update for immediate UI feedback
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, status: newStatus } : u)),
    );

    // 2. Persistent Backend Update
    const result = await updateCompanyStatus(userId, newStatus);

    // 3. Refresh to ensure latest data sync
    if (!result.success) {
      console.error(result.message);
      loadCompanies();
    }
  };

  const handleModalStatusChange = (userId, newStatus) => {
    handleStatusToggle(userId, newStatus);
    setSelectedUser(null);
  };

  const handlePlanChange = async (userId, subscriptionPayload) => {
    const result = await updateCompanyPlan(userId, subscriptionPayload);

    if (result.success) {
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, subscription: result.data || { ...u.subscription, ...subscriptionPayload } }
            : u,
        ),
      );
      setSelectedUser((prev) =>
        prev && prev.id === userId
          ? {
              ...prev,
              subscription: result.data || {
                ...prev.subscription,
                ...subscriptionPayload,
              },
            }
          : prev,
      );

      await Swal.fire({
        icon: "success",
        title: "Plan Updated",
        text: result.message || "User plan synchronized successfully.",
        confirmButtonColor: "#0B1C2D",
        timer: 2200,
        showConfirmButton: false,
      });
    } else {
      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: result.message || "Could not update user plan.",
        confirmButtonColor: "#E63946",
      });
    }
  };

  // Stats Calculation based on normalized status
  const stats = {
    total: users.length,
    active: users.filter((u) => u.status === true || u.status === "Active")
      .length,
    recent: users.filter((u) => {
      const joinedAt = u.createdAt ? new Date(u.createdAt) : new Date();
      const diff = Math.abs(new Date() - joinedAt);
      return Math.ceil(diff / (1000 * 60 * 60 * 24)) <= 7;
    }).length,
  };

  const exportToExcel = (data) => {
    if (!data || data.length === 0) {
      alert("No data available to export.");
      return;
    }

    // 1. Define the Headers (Columns in Excel)
    const headers = [
      "Company Name",
      "Admin Name",
      "Email",
      "Mobile",
      "Business Type",
      "Country",
      "Plan",
      "Status",
      "Created At",
    ];

    // 2. Map the data to CSV rows
    // We extract nested values like subscription.plan specifically
    const csvRows = data.map((user) =>
      [
        `"${user.companyName || "N/A"}"`,
        `"${user.name || "N/A"}"`,
        `"${user.email || "N/A"}"`,
        `"${user.mobile || "N/A"}"`,
        `"${user.businessType || "Other"}"`,
        `"${user.country || "N/A"}"`,
        `"${user.subscription?.plan || "Free"}"`,
        user.status ? "Active" : "Inactive",
        `"${user.createdAt || ""}"`,
      ].join(","),
    );

    // 3. Combine headers and rows
    const csvContent = [headers.join(","), ...csvRows].join("\n");

    // 4. Create a Blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `ContactHQ_Dataset_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`${pageShellClass}`}>
      <div className={pageContentClass}>
        <div className="flex flex-col xl:flex-row xl:items-end justify-between gap-4 sm:gap-6 mb-6 lg:mb-10">
          <header className="hidden lg:block">
            <h1 className="text-3xl font-black text-[#0B1C2D] tracking-tighter uppercase">
              Orchestrator <span className="text-red-500">Companies</span>
            </h1>
            <p className="text-sm text-slate-400 font-medium mt-1">
              Monitoring {stats.total} total assets across your platform.
            </p>
          </header>

          <div className="flex gap-3 sm:gap-4 w-full xl:w-auto">
            <StatCard
              label="Active"
              value={stats.active}
              color="bg-green-500"
            />
            <StatCard
              label="New (7D)"
              value={stats.recent}
              color="bg-blue-500"
            />
          </div>
        </div>

        <FilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onExport={() => exportToExcel(filteredUsers)}
        />

        {isLoading ? (
          <div className="py-20 text-center text-slate-400 font-bold uppercase tracking-widest animate-pulse">
            Fetching orchestrated data...
          </div>
        ) : (
          <UserTable
            users={currentData}
            currentPage={currentPage}
            totalPages={totalPages}
            onSelectUser={setSelectedUser}
            onPageChange={setCurrentPage}
            onStatusToggle={handleStatusToggle}
          />
        )}

        <UserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onStatusChange={handleModalStatusChange}
          onPlanChange={handlePlanChange}
        />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-white flex-1 xl:flex-none px-4 sm:px-6 py-3 sm:py-4 rounded-2xl sm:rounded-[1.5rem] shadow-sm border border-slate-100 xl:min-w-[140px]">
    <div className="flex items-center gap-2 mb-1">
      <div className={`w-2 h-2 rounded-full ${color}`}></div>
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
        {label}
      </span>
    </div>
    <div className="text-xl sm:text-2xl font-black text-[#0B1C2D]">{value}</div>
  </div>
);

export default Users;
